import type { ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';

import type { SerializableNode, SerializerProps } from '@/serializer';
import type { TSwitchElement } from '@/switchElement';
import { updateNodeText } from '@/utils/updateNodeText';

import { getRecommendationFormField } from '../utils/getRecommendationFormField';

import { getStructureElement } from '../../serializer/getStructureElement';
import { SwitchElement } from '../../switchElement/Switch';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import type { Recommendation } from '../../types';

interface ExtendedSerializerProps extends SerializerProps {
  node: JSONContent & SerializableNode;
  recommendation: Recommendation;
  onChange: (recommendation: Recommendation) => void;
  isChecked: boolean;
  isLive: boolean;
  isSelectAllChecked: boolean;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    recommendation,
    onChange,
    isChecked,
    isLive,
    isSelectAllChecked,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);
  // The components short-circuited below never reach getStructureElement.
  const nodeId = node?.attrs?.id;
  const dataAttributes = {
    'data-json-path': path.join(','),
    ...(nodeId ? { 'data-node-id': String(nodeId) } : {}),
  };

  //TODO: Add serializer for recommendations
  const isImage = node?.type === 'image';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';
  const mergeTag = node?.attrs?.mergeTag;
  const isRootContainerImage = node?.type === 'container' && rootStyle && node?.attrs?.backgroundImage;

  if (isRootContainerImage) {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        backgroundImage: recommendation?.logo_url,
      },
    } as JSONContent & SerializableNode;
  }

  // Swap out images
  if (isImage && mergeTag) {
    if (mergeTag === 'recommendation.image') {
      node = produce(node, (draft) => {
        if (draft.attrs) {
          draft.attrs.src = recommendation?.logo_url;
          draft.attrs.alt = recommendation?.name;
        }
      });
    }
  }

  if (mergeTag === 'recommendation.switch' && node.type === 'switch') {
    const formField = getRecommendationFormField(recommendation);
    return (
      <SwitchElement
        id={node.attrs?.id}
        onCheckedChange={() => onChange(recommendation)}
        checked={isChecked || isSelectAllChecked}
        element={node as TSwitchElement}
        attributes={dataAttributes}
        isLive={isLive}
        isSelectAllChecked={isSelectAllChecked}
        name={formField.name}
        value={formField.value}
      >
        {null}
      </SwitchElement>
    );
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && mergeTag) {
    if (mergeTag === 'recommendation.sponsored' && !recommendation?.sponsored) {
      return null;
    }
    if (mergeTag === 'recommendation.sponsored' && recommendation?.sponsored) {
      node = updateNodeText(node, 'Sponsored') as JSONContent & SerializableNode;
    }

    if (mergeTag === 'recommendation.name') {
      node = updateNodeText(node, recommendation?.name) as JSONContent & SerializableNode;
    }
    if (mergeTag === 'recommendation.publisher') {
      if (!recommendation?.publisher_name) {
        return null;
      }
      node = updateNodeText(node, `by ${recommendation.publisher_name}`) as JSONContent & SerializableNode;
    }
    if (mergeTag === 'recommendation.description') {
      node = updateNodeText(node, recommendation?.description || '') as JSONContent & SerializableNode;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          recommendation,
          onChange,
          isChecked,
          isLive,
          isSelectAllChecked,
        },
        [...path, i],
      ),
    ) || [];

  return getStructureElement({
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
    jsonPath: path,
  });
};

const PUBLISHER_MERGE_TAG = 'recommendation.publisher';
const NAME_MERGE_TAG = 'recommendation.name';

const containsPublisherNode = (node?: JSONContent | null): boolean => {
  if (!node) {
    return false;
  }
  if (node.attrs?.mergeTag === PUBLISHER_MERGE_TAG) {
    return true;
  }
  return (node.content ?? []).some((child) => containsPublisherNode(child));
};

// Builds a publisher line by cloning the name node so it stays schema-valid and
// inherits theming, then dialing it down to a smaller, lighter style.
const buildPublisherNodeFromName = (nameNode: JSONContent): JSONContent =>
  produce(nameNode, (draft) => {
    if (draft.attrs) {
      draft.attrs.mergeTag = PUBLISHER_MERGE_TAG;
      draft.attrs.lp_name = 'Publisher';
      if (typeof draft.attrs.id === 'string') {
        draft.attrs.id = `${draft.attrs.id}-publisher`;
      }
    }

    const applyMutedStyle = (textNode?: JSONContent) => {
      if (!textNode) {
        return;
      }
      textNode.marks?.forEach((mark) => {
        if (mark.type === 'textStyle' && mark.attrs) {
          mark.attrs.fontSize = '14px';
          mark.attrs.mobileFontSize = '14px';
          mark.attrs.fontWeight = '400';
        }
      });
      textNode.content?.forEach(applyMutedStyle);
    };

    draft.content?.forEach(applyMutedStyle);
  });

// Existing recommendation cards were saved before the publisher node existed, so
// inject one right after the name when the recommendation has a publisher and the
// stored structure doesn't already include a publisher node (avoids duplicates on
// newer cards that carry the editable node).
const withFallbackPublisherNode = (
  root: JSONContent & SerializableNode,
  publisherName?: string | null,
): JSONContent & SerializableNode => {
  if (!publisherName || containsPublisherNode(root)) {
    return root;
  }

  return produce(root, (draft) => {
    const insertAfterName = (node: JSONContent): boolean => {
      if (!node.content) {
        return false;
      }
      const nameIndex = node.content.findIndex((child) => child.attrs?.mergeTag === NAME_MERGE_TAG);
      if (nameIndex !== -1) {
        node.content.splice(nameIndex + 1, 0, buildPublisherNodeFromName(node.content[nameIndex]));
        return true;
      }
      return node.content.some((child) => insertAfterName(child));
    };

    insertAfterName(draft);
  });
};

export function RecommendationsSerializer(props: ExtendedSerializerProps) {
  const node = withFallbackPublisherNode(props.node, props.recommendation?.publisher_name);
  return <>{serializeNode({ ...props, node })}</>;
}
